import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";

/**
 * Handles automatic redirect from /chat or /spectra/:space_id/chat to a new chat with generated ID.
 * Only runs once per session to prevent infinite loops.
 * 
 * @param {string|undefined} chat_id - Current chat ID from URL params
 * @param {string|undefined} space_id - Optional space ID for Spectra chats
 * @param {object} user - User object with uid
 * @returns {void}
 */
export const useNewChatRedirect = (chat_id, space_id, user) => {
  const navigate = useNavigate();
  const hasCreatedNewChatRef = useRef(false);
  const lastChatIdRef = useRef(chat_id);

  useEffect(() => {
    if (chat_id === undefined && user?.uid) {
      // Reset the guard if chat_id changed from defined to undefined (new spark clicked)
      if (lastChatIdRef.current !== undefined) {
        hasCreatedNewChatRef.current = false;
      }

      // Only run if not already created for this session
      if (hasCreatedNewChatRef.current) {
        return;
      }

      const ch_uid = uuidv4();
      hasCreatedNewChatRef.current = true;
      lastChatIdRef.current = ch_uid;
      
      // Navigate to new chat URL
      if (space_id) {
        navigate(`/spectra/${space_id}/chat/${ch_uid}`);
      } else {
        navigate(`/chat/${ch_uid}`);
      }
    }
  }, [chat_id, user?.uid, space_id, navigate]);
};
